import qs from 'query-string';
import errorMap from './errorUtils';
import store from './store';

/**
 * Returns Parent window of the current page.
 *
 * @param {Window} currentWindow
 * @returns {Window}
 */
function getParentWindow(currentWindow = window) {
  return currentWindow.parent;
}

/**
 * Returns a customized IFrame element which loads connect.smallcase.com
 *
 * **This DOESN'T append the element to DOM**
 *
 * @returns {HTMLIFrameElement}
 */
function prepareIFrame(url) {
  const iframeElement = document.createElement('iframe');
  iframeElement.setAttribute('src', url);
  iframeElement.setAttribute('sandbox', 'allow-scripts allow-forms allow-popups allow-same-origin allow-top-navigation-by-user-activation');
  iframeElement.style.position = 'fixed';
  iframeElement.style.top = '50%';
  iframeElement.style.left = '50%';
  iframeElement.style.transform = 'translate(-50%, -50%)';
  iframeElement.style.width = '100vw';
  iframeElement.style.height = '100vh';
  iframeElement.style.minHeight = '360px';
  iframeElement.style.borderWidth = '0';
  iframeElement.style.zIndex = '+2147483647'; // safe highest allowed value
  // iframeElement.style.backgroundColor = 'white';
  // iframeElement.style.boxShadow = '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)';
  return iframeElement;
}


/**
 * Get HTMLElement from elementOrSelector, which can be a selector or an actual HTMLElement.
 *
 * @param {string | HTMLElement} elementOrSelector
 * @returns {HTMLElement}
 */
function getHtmlElement(elementOrSelector) {
  if (typeof elementOrSelector === 'string') {
    const element = document.querySelector(elementOrSelector);

    if (!(element instanceof HTMLElement)) {
      throw errorMap.INVALID_ELEMENTORSELECTOR;
    }

    return element;
  } else {
    const element = elementOrSelector;

    if (!(element instanceof HTMLElement)) {
      throw errorMap.INVALID_ELEMENTORSELECTOR;
    }

    if (element === null) {
      throw errorMap.INVALID_ELEMENTORSELECTOR;
    }
    return element;
  }
}

/**
 * @returns {{promise: Promise<MessageEvent>, cancel: () => void}}
 */
function listenMessageEvent(messageType) {
  const returnObj = {
    promise: undefined,
    cancel: undefined,
  };

  returnObj.promise = new Promise((resolve) => {
    const modifiedResolve = (ev) => {
      if (ev.data.type && ev.data.type.toLowerCase() === messageType.toLowerCase()) {
        resolve(ev);
      }
    };

    returnObj.cancel = () => {
      window.removeEventListener('message', modifiedResolve);
    };

    window.addEventListener('message', modifiedResolve);
  });

  return returnObj;
}

function sendPostMessage({ messageType, messageParams = {} } = {}) {
  switch (store.deviceType) {
    case 'ios':
      try {
        window.webkit.messageHandlers.postToNativeSdk.postMessage(
          JSON.stringify({ type: messageType, ...messageParams }),
        );
      } catch (e) {
        console.log('IOS bridge method not found');
      }
      break;

    case 'android':
      try {
        window.AndroidBridge.postToNativeSdk(
          JSON.stringify({ type: messageType, ...messageParams }),
        );
      } catch (e) {
        console.log('Android bridge method not found');
      }
      break;

    case 'web':
    default:
      /* eslint-disable no-case-declarations */
      const topWindow = getParentWindow();
      const messageObj = {
        type: messageType,
        ...messageParams,
      };
      /* eslint-enable no-case-declarations */

      ENV !== 'production' && console.log(`middleframe - Trying to send PostMessage: ${JSON.stringify(messageObj, null, 2)}`);

      if (topWindow !== window) {
        topWindow.postMessage(
          messageObj,
          '*',
        );
      }
      break;
  }
}

function getTimeLeftFromURL() {
  const queryParamsObj = qs.parse(window.location.search);
  const { t: timeLeft } = queryParamsObj;

  return timeLeft;
}

function getReasonFromURL() {
  const queryParamsObj = qs.parse(window.location.search);
  const { reason } = queryParamsObj;

  return reason;
}

function getBrokerUserIDFromURL() {
  const queryParamsObj = qs.parse(window.location.search);
  const { brokerUserID } = queryParamsObj;
  return brokerUserID;
}

async function check3pCookies() {
  if (navigator.brave && await navigator.brave.isBrave()) return false;
  document.cookie = 'checkthirdparty=yes; Max-Age=3600; SameSite=None; Secure';
  const support = /checkthirdparty=yes/.test(document.cookie);
  document.cookie = 'checkthirdparty=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  return support;
}

function getMobileContext() {
  const width = document.documentElement.clientWidth;
  return ({
    desktop: (width >= 1184),
    tabletOrLower: (width <= 1183),
    tablet: (width <= 1183) && (width > 768),
    mobile: (width <= 768) && (width >= 360),
    smaller: (width <= 359),
  });
}

function getInnerHeight() {
  return window.innerHeight;
}

// source: tickertape-web
function getTouchDirection(touchStart, currentTouch) {
  const delta = {
    x: currentTouch.pageX - touchStart.pageX,
    y: -1 * (currentTouch.pageY - touchStart.pageY),
  };

  const perctChange = {
    x: (delta.x / touchStart.pageX) * 100,
    y: (delta.y / touchStart.pageY) * 100,
  };

  const slope = delta.x !== 0 ?
    delta.y / delta.x : (
      Math.sign(delta.y) * 10000
    );

  const touchDirection = {
    delta,
    slope,
    perctChange,
  };

  if (Math.abs(slope) > 5) {
    touchDirection.orientation = 'VERTICAL';
    if (Math.sign(delta.y) > 0) {
      touchDirection.direction = 'UP';
    } else {
      touchDirection.direction = 'DOWN';
    }
  } else if (Math.abs(slope) < 0.2) {
    touchDirection.orientation = 'HORIZONTAL';
    if (Math.sign(delta.x) < 0) {
      touchDirection.direction = 'LEFT';
    } else {
      touchDirection.direction = 'RIGHT';
    }
  }

  return touchDirection;
}

export {
  getParentWindow,
  prepareIFrame,
  getHtmlElement,
  listenMessageEvent,
  sendPostMessage,
  getTimeLeftFromURL,
  getReasonFromURL,
  getBrokerUserIDFromURL,
  check3pCookies,
  getMobileContext,
  getInnerHeight,
  getTouchDirection,
};



// WEBPACK FOOTER //
// ./utils/browserUtils.js